import { ColumnDef } from '@tanstack/react-table';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../../context';
import { useToast } from '../../../../../context';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetAllTerritoriesQuery } from '../../../../../services/endpoints/places/territory';
import { getCurrentUser } from '../../../../../services/helper';
import { SFCLocation, SFCTerritory } from '../../../../../types/place.types';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';
import { Forbidden } from '../../../../../components/error/Forbidden';
import { canEditTerritory, canViewTerritories } from '../../../../../utils/rbac';
import { isGlobal } from '../../../../../utils/consts';

interface DataRow extends SFCTerritory {}

// =============================================================================
// COMPONENT
// =============================================================================

export const TerritoriesView = () => {
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const userHasAccess = canViewTerritories(currentUser);
  const isGlobalUser = currentUser ? isGlobal(currentUser.user_type_id) : false;

  const [filters, setFilters] = useListFiltersInUrl(
    { scope: 'all', data: { page: 1, entries: 20 } },
    { omitScopeFromUrl: true }
  );
  const { data, error, isLoading, isFetching } = useGetAllTerritoriesQuery(filters, {
    skip: !userHasAccess,
    refetchOnMountOrArgChange: true
  });

  const filterFormFields: FilterField[] = [
    {
      name: 'title',
      label: 'Title',
      value: getFilterValue(filters.data?.filterBy, 'title')
    },
    ...(isGlobalUser
        ? [
          {
            name: 'location_id',
            label: 'Location',
            type: 'number' as const,
            element: (
                <FormSelect
                    name="location_id"
                    label="Location"
                    options={locationOptions}
                    selected_Value={locationOptions.find(
                        (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
                    )}
                />
            )
          }
        ]
        : [])
  ];

  const columns: ColumnDef<DataRow>[] = [
    {
      header: 'ID',
      accessorKey: 'id'
    },
    {
      header: 'Title',
      accessorKey: 'title'
    },
    {
      header: 'Location',
      accessorKey: 'location',
      enableSorting: false,
      cell: ({ getValue }) => {
        let location = getValue() as SFCLocation;
        return ( location ?
                <LinkCell
                    content={location?.title ?? 'error'}
                    url={`/admin/places/locations/${location?.id ?? 0}`}
                /> : 'No Location Assigned'
        );
      }
    },
    {
      header: '',
      accessorKey: 'blank',
      enableSorting: false,
      cell: ({ row }) => (
          <>
            <LinkCell
                content="View"
                url={`/admin/places/territories/${row.original.id}`}
            />
            &nbsp; &nbsp;
            {canEditTerritory(currentUser) && (
                <LinkCell
                    content="Edit"
                    url={`/admin/places/territories/${row.original.id}/edit`}
                />
            )}
          </>
      )
    }
  ];

  // Handle API errors with better messaging
  useEffect(() => {
    if (error) {
      let errorMessage = 'Error while loading territories';

      // Extract error message from API response
      if ('data' in error && typeof error.data === 'object' && error.data !== null) {
        const apiError = error.data as { message?: string };
        if (apiError.message) {
          errorMessage = apiError.message;
        }
      } else if ('message' in error && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      addToast(errorMessage, 'error');
    }
  }, [error, addToast]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  // Check permissions before rendering
  if (!userHasAccess) {
    return <Forbidden />;
  }

  if (isLoading) {
    return <></>;
  }

  return (
      <div className="py-2">
        <SFCTable
            name={'Territories'}
            columns={columns}
            defaultScope={filters.scope ?? 'all'}
            data={data?.results ?? []}
            count={data?.count}
            isLoading={isLoading}
            isFetching={isFetching}
            indexDownloadPath="territories"
            filters={filters?.data?.filterBy}
            page_Size={filters?.data?.entries}
            filterForm={
              <FilterForm
                  fields={filterFormFields}
                  onApplyFilters={setFilterBy}
              />
            }
            onFiltersChanged={setFilters}
            newItemEl={
              canEditTerritory(currentUser) ? (
                  <SimpleButton
                      variant="sfc-blueberry"
                      height={40}
                      truncate={false}
                      onClick={() => navigate(`/admin/places/territories/new`)}
                  >
                    New Territory
                  </SimpleButton>
              ) : undefined
            }
        />
      </div>
  );
};